'use client';
import { SITE_ALERT_EMAIL_ON } from '@/lib/retention/site-alert-switch';

/**
 * retention-1031 — 현장 상세 「{현장명} 일정 알림」 블록(리드폼 바로 아래 · 로그인 없이).
 *
 *   · 카톡방 버튼 「{현장명} 소식 카톡방에서 받기」 — 슬롯 'site_alert'(talk-banner · user_events 한 곳). 노출은 화면 진입 1회.
 *   · 이메일 칸 + 동의 1 → POST /api/apt/site-alert → 확인 메일(이중 동의). 확인 전에는 아무것도 보내지 않는다.
 *   · 인앱 브라우저(네이버 46% · 웹 푸시 불가)는 카톡방이 주 버튼(lib/retention/site-alert-client). 서버 HTML 은 방문자와 무관하게 같다.
 *   · 계측: user_events event_name 'site_alert' — view(화면 진입) · start(첫 입력) · submit(결과 status). 개인정보(이메일)는 싣지 않는다.
 *   · 503(준비 안 됨: 메일 키·서명 비밀·표 열) = 이메일 칸을 접고 카톡방을 주 버튼으로.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { KAKAO_TALK_URL, talkSiteLabel, trackTalkClick } from '@/lib/talk-banner';
import { useTalkView } from '@/components/banner/useTalkView';
import { useInAppBrowser } from '@/hooks/useInAppBrowser';
import { track } from '@/lib/analytics';
import { SITE_ALERT_EMAIL_RE, SITE_ALERT_MSG, siteAlertPrimary, siteAlertStateOf, type SiteAlertSubmitState } from '@/lib/retention/site-alert-client';
import './site-alert.css';

export interface SiteAlertSubscribeProps {
  siteSlug: string;
  siteName: string;
}

const SITE_ALERT_ANCHOR = 'site-alert';


export default function SiteAlertSubscribe({ siteSlug, siteName }: SiteAlertSubscribeProps) {
  const inApp = useInAppBrowser();
  const [state, setState] = useState<SiteAlertSubmitState>('idle');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const started = useRef(false);
  const rootRef = useRef<HTMLElement>(null);
  const primary = !SITE_ALERT_EMAIL_ON || state === 'unavailable' ? 'talk' : inApp.resolved ? siteAlertPrimary(inApp) : 'email';
  const talkRef = useTalkView<HTMLAnchorElement>('site_alert', { site_slug: siteSlug, copy: 'site', inapp: inApp.type ?? 'none' });

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) {
        track('view', 'site_alert', { site_slug: siteSlug, inapp: inApp.type ?? 'none' });
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteSlug, inApp.resolved]);

  const markStart = () => {
    if (started.current) return;
    started.current = true;
    track('start', 'site_alert', { site_slug: siteSlug });
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === 'sending') return;
    const v = email.trim();
    if (!SITE_ALERT_EMAIL_RE.test(v)) { setErr('이메일 주소를 확인해 주세요'); return; }
    if (!consent) { setErr('알림 메일 수신에 동의해 주세요'); return; }
    setErr(null);
    setState('sending');
    let next: SiteAlertSubmitState = 'error';
    let msg: string | null = null;
    try {
      const r = await fetch('/api/apt/site-alert', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: siteSlug, email: v, consent: true, hp }),
      });
      const body = await r.json().catch(() => null);
      next = siteAlertStateOf(r.status, body);
      if (next === 'error') msg = typeof body?.error === 'string' ? body.error : '잠시 후 다시 시도해 주세요';
    } catch {
      msg = '잠시 후 다시 시도해 주세요';
    }
    setState(next);
    setErr(msg);
    track('submit', 'site_alert', { site_slug: siteSlug, status: next, inapp: inApp.type ?? 'none' });
  }

  const done = state === 'sent' || state === 'pending' || state === 'already' || state === 'unavailable';
  return (
    <section ref={rootRef} id={SITE_ALERT_ANCHOR} className="kd-sa" data-sa-primary={primary} aria-labelledby="kd-sa-h">
      <p id="kd-sa-h" className="kd-sa-h">{siteName} 일정 알림</p>
      <p className="kd-sa-p">{SITE_ALERT_EMAIL_ON ? '모집공고·견본주택·청약 일정이 바뀌면 알려드립니다. 로그인 없이 신청하고 언제든 끌 수 있습니다.' : '모집공고·견본주택·청약 일정 소식을 카톡방에서 가장 먼저 받아 보세요.'}</p>
      <div className="kd-sa-acts">
        <a
          ref={talkRef}
          className="kd-sa-talk"
          href={`${KAKAO_TALK_URL}?utm_source=site_alert`}
          target="_blank"
          rel="noopener noreferrer"
          data-talk-slot="site_alert"
          onClick={() => trackTalkClick('site_alert', { site_slug: siteSlug, copy: 'site', inapp: inApp.type ?? 'none' })}
        >
          {talkSiteLabel(siteName)}
        </a>
        {/* 2026-10-31 Node: 메일 자동 발송 중단 — 메일 신청 폼은 SITE_ALERT_EMAIL_ON 일 때만(지금은 카톡방만). */}
        {!SITE_ALERT_EMAIL_ON ? null : done ? (
          <p className="kd-sa-msg" role="status">{SITE_ALERT_MSG[state as keyof typeof SITE_ALERT_MSG]}</p>
        ) : (
          <form className="kd-sa-form" onSubmit={onSubmit} onFocusCapture={markStart} noValidate>
            <div className="kd-sa-row">
              <label htmlFor="kd-sa-email" className="sr-only">이메일</label>
              <input
                id="kd-sa-email"
                className="kd-sa-in"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="이메일 주소"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (err) setErr(null); }}
                aria-invalid={!!err}
                aria-describedby={err ? 'kd-sa-err' : undefined}
              />
              <button type="submit" className="kd-sa-btn" disabled={state === 'sending'}>{state === 'sending' ? '보내는 중' : '메일로 받기'}</button>
            </div>
            <label className="kd-sa-consent">
              <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); if (err) setErr(null); }} />
              <span>이 현장 일정 알림 메일 수신에 동의합니다(이메일만 보관 · 해지 시 중단 · <a href="/privacy">개인정보 처리방침</a>)</span>
            </label>
            <input className="kd-sa-hp" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={hp} onChange={(e) => setHp(e.target.value)} />
            {err ? <p id="kd-sa-err" className="kd-sa-msg" data-tone="err" role="alert">{err}</p> : null}
          </form>
        )}
      </div>
    </section>
  );
}
